import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import type {
	Experience,
	ExperienceFlag
} from '@/features/experience-shell/types.ts'
import {
	type HttpEditExperienceItemFlags,
	httpAddExperienceItemFlags
} from '@/features/experience-vetting/http/experience-vetting.http.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'

export const useAddExperienceGalleryImageFlags = (
	urlParams: Omit<
		HttpEditExperienceItemFlags['urlParams'],
		'section' | 'item' | 'itemId'
	>,
	options?: Omit<
		UseMutationOptions<
			unknown,
			unknown,
			{ flags: ExperienceFlag[]; itemId: number }
		>,
		'mutationFn'
	>
) => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (payload) =>
			httpAddExperienceItemFlags({
				urlParams: {
					...urlParams,
					section: 'galleries',
					item: 'images',
					itemId: payload.itemId
				},
				payload: { flagIds: payload.flags.map((flag) => flag.id) },
				params: {
					response_type: 'small'
				}
			}),
		onError: (error, variables, onMutateResult, context) => {
			queryClient.setQueriesData(
				{
					queryKey: [
						QUERY_KEYS.EXPERIENCE_VETTING,
						urlParams.experienceId
					]
				},
				context
			)
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onMutate: async (variables) => {
			const queryKey = [
				QUERY_KEYS.EXPERIENCE_VETTING,
				urlParams.experienceId
			]

			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous values
			const previousData = queryClient.getQueryData<Experience>(queryKey)

			// Update cached data
			if (previousData) {
				queryClient.setQueriesData<Experience>(
					{ queryKey },
					{
						...previousData,
						gallery: {
							...previousData.gallery,
							images: previousData.gallery.images.map((image) => {
								if (image.id === variables.itemId) {
									return {
										...image,
										flags: [
											...image.flags,
											...variables.flags
										]
									}
								}
								return image
							})
						}
					}
				)
			}
			return previousData
		},
		...options
	})
}
